/**
 * GTCC-BAU G2b/G2c/G2d/G2e/G3a — read-only Ask Genius KPI summaries for the
 * Control, Observability, Administration, Communication, Governance
 * and Security nav groups. The registry maps every concrete view onto this
 * closed set of evidence-backed summary variants.
 *
 * Each summary reuses the admin app's existing `apiGet`/react-query
 * conventions and the existing per-feature API modules (data-plane, db-ops)
 * rather than introducing a new fetch path. Queries only run while the Ask
 * Genius panel is open (`enabled`) — no polling, no prefetch, no writes.
 *
 * All views render their KPIs through the shared `Tile` component (Label /
 * Value / Timestamp) and the shared `formatCount`/`formatIsoOrDash`
 * formatters, so every view looks and behaves the same way. Loading/empty/
 * error handling is unified via `QuerySection`, which already delegates to
 * the design-system's `LoadingState`/`EmptyState`/`ErrorState` primitives.
 */
import { EmptyState, ErrorState, LoadingState } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { planNameKey, type PlanId } from "@genius-tracker/i18n";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { ReactNode } from "react";

import { apiGet } from "../lib/api.js";
import { fetchDataPlaneOverview } from "../data-plane/api.js";
import type { DataPlaneHealthOverview } from "../data-plane/types.js";
import { fetchDbOpsReadiness } from "../db-ops/api.js";
import type { DbOpsReadinessReport } from "../db-ops/types.js";
import type { DomainRoutingHealthResponse } from "../domain-routing/types.js";
import { MONITORING_SUMMARY_PATH } from "../monitoring/api.js";
import type { MonitoringSummaryDto } from "../monitoring/types.js";
import { formatCount, formatIsoOrDash } from "../monitoring/utils.js";
import type { ProviderRegistry } from "../tenants/tenantAiCapabilitiesEditValidation.js";
import { FEATURE_FLAGS_KEY, type FeatureFlagsResponse } from "../lib/featureFlags.js";
import { resolveMinWidthBreakpointQuery, useBreakpointMatch } from "./breakpoint.js";
import { GtccCapHint } from "./GtccCapHint.js";
import type { GtccAskGeniusSummaryKey } from "./registry.js";
import { tallySmokeQueries, useGtccSmokeQueries } from "./views/smoke/useGtccSmoke.js";

interface AskGeniusSummaryProps {
  readonly summaryKey: GtccAskGeniusSummaryKey;
  readonly enabled: boolean;
}

export function AskGeniusSummary({ summaryKey, enabled }: AskGeniusSummaryProps) {
  const { t } = useLocale();
  switch (summaryKey) {
    case "launch":
      return <LaunchOverviewSummary enabled={enabled} />;
    case "risk":
      return <RiskRadarSummary enabled={enabled} />;
    case "alin":
      return <AlinSummary enabled={enabled} />;
    case "cockpit":
      return <CockpitSummary enabled={enabled} />;
    case "diag":
      return <DiagnosticsSummary enabled={enabled} />;
    case "dplane":
      return <DataPlaneSummary enabled={enabled} />;
    case "aia":
      return <AiaSummary enabled={enabled} />;
    case "ten":
      return <TenantsSummary enabled={enabled} />;
    case "hw":
      return <HardwareSummary enabled={enabled} />;
    case "gw":
      return <GatewaySummary enabled={enabled} />;
    case "dom":
      return <DomainRoutingSummary enabled={enabled} />;
    case "mail":
      return <MailSummary enabled={enabled} />;
    case "notifications":
      return <NotificationsSummary enabled={enabled} />;
    case "audit":
      return <AuditSummary enabled={enabled} />;
    case "pset":
      return <PlatformSettingsSummary enabled={enabled} />;
    case "security":
      return <SecuritySummary enabled={enabled} />;
    case "dz":
      return <DangerZoneSummary enabled={enabled} />;
    case "smoke":
      return <SmokeSummary enabled={enabled} />;
    default:
      return <p data-testid="gtcc-ask-genius-summary-none">{t("admin.gtcc.ask_genius.none")}</p>;
  }
}

function QuerySection<T>({
  query,
  loadingLabel,
  errorTitle,
  emptyTitle,
  isEmpty,
  render,
}: {
  readonly query: UseQueryResult<T, Error>;
  readonly loadingLabel: string;
  readonly errorTitle: string;
  readonly emptyTitle: string;
  readonly isEmpty: (data: T) => boolean;
  readonly render: (data: T) => ReactNode;
}) {
  if (query.isPending) return <LoadingState label={loadingLabel} />;
  if (query.isError) return <ErrorState title={errorTitle} body={query.error.message} />;
  if (isEmpty(query.data)) return <EmptyState title={emptyTitle} />;
  return <>{render(query.data)}</>;
}

/**
 * Shared responsive grid for a summary's KPI tiles: multi-column at/above
 * the shared GTCC breakpoint token, single column below it — the same
 * token the Mono-Deck nav collapses at, so the Tile grid and the nav change
 * layout together instead of drifting independently (audit finding G3-6).
 */
function TileGrid({ testId, children }: { readonly testId: string; readonly children: ReactNode }) {
  const multiColumn = useBreakpointMatch(resolveMinWidthBreakpointQuery);
  return (
    <div
      className="gtcc-ask-genius-tile-grid"
      data-testid={testId}
      style={{
        display: "grid",
        gap: "var(--gt-sys-space-3, 12px)",
        gridTemplateColumns: multiColumn ? "repeat(auto-fit, minmax(180px, 1fr))" : "1fr",
      }}
    >
      {children}
    </div>
  );
}

/**
 * Shared KPI tile anatomy (Label / Value / optional Timestamp) used by every
 * Ask Genius summary view. `value` is a plain string so a meaningful
 * `aria-label` can always be derived from it. Pass `ariaLabel` to override
 * the default `${label}: ${value}` template when `value` already embeds its
 * own colon-separated sub-label (e.g. `"ready: 3"`), which would otherwise
 * produce a doubled colon.
 */
function Tile({
  label,
  value,
  timestamp,
  ariaLabel,
  note,
}: {
  readonly label: string;
  readonly value: string;
  readonly timestamp?: string | null;
  readonly ariaLabel?: string;
  readonly note?: string | undefined;
}) {
  return (
    <div
      className="gtcc-ask-genius-tile"
      data-testid="gtcc-ask-genius-tile"
      aria-label={ariaLabel ?? `${label}: ${value}${note ? ` ${note}` : ""}`}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--gt-sys-space-1, 4px)",
        padding: "var(--gt-sys-space-3, 12px)",
        border: "1px solid var(--gt-sys-border, #e5e7eb)",
        borderRadius: "var(--gt-sys-radius-md, 8px)",
        background: "var(--gt-sys-surface-raised, #f9fafb)",
      }}
    >
      <span
        className="gtcc-ask-genius-tile-label"
        style={{
          fontSize: "var(--gt-sys-font-size-xs, 12px)",
          color: "var(--gt-sys-text-muted, #6b7280)",
        }}
      >
        {label}
      </span>
      <span
        className="gtcc-ask-genius-tile-value"
        style={{
          fontSize: "var(--gt-sys-font-size-lg, 15px)",
          fontWeight: 600,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </span>
      {timestamp !== undefined ? (
        <span
          className="gtcc-ask-genius-tile-timestamp"
          style={{
            fontSize: "var(--gt-sys-font-size-xs, 12px)",
            color: "var(--gt-sys-text-muted, #6b7280)",
          }}
        >
          {formatIsoOrDash(timestamp)}
        </span>
      ) : null}
      {note ? (
        <span
          className="gtcc-ask-genius-tile-timestamp"
          style={{
            fontSize: "var(--gt-sys-font-size-xs, 12px)",
            color: "var(--gt-sys-text-muted, #6b7280)",
          }}
        >
          {note}
        </span>
      ) : null}
    </div>
  );
}

interface OverviewRow {
  readonly overall: "ready" | "needs_action" | "blocked" | "manual";
}
interface OverviewResponse {
  readonly items: readonly OverviewRow[];
  readonly total: number;
  readonly capped: boolean;
}

function LaunchOverviewSummary({ enabled }: { readonly enabled: boolean }) {
  const { t } = useLocale();
  const query = useQuery<OverviewResponse, Error>({
    queryKey: ["gtcc", "ask-genius", "customer-launch-overview"],
    queryFn: ({ signal }) =>
      apiGet<OverviewResponse>("/v1/admin/customer-launch-overview", undefined, signal),
    enabled,
  });
  return (
    <QuerySection
      query={query}
      loadingLabel={t("admin.gtcc.ask_genius.launch.loading")}
      errorTitle={t("admin.gtcc.ask_genius.launch.error")}
      emptyTitle={t("admin.gtcc.ask_genius.no_tenants_to_summarize")}
      isEmpty={(data) => data.total === 0}
      render={(data) => {
        const byStatus = data.items.reduce<Record<string, number>>((acc, row) => {
          acc[row.overall] = (acc[row.overall] ?? 0) + 1;
          return acc;
        }, {});
        const tenantsValue = t("admin.gtcc.ask_genius.tenants_count", { count: data.total });
        return (
          <TileGrid testId="gtcc-ask-genius-summary-launch">
            <Tile
              label={t("admin.gtcc.ask_genius.label.tenants")}
              value={`${tenantsValue}${data.capped ? ` ${t("admin.gtcc.ask_genius.launch.capped_suffix")}` : ""}`}
            />
            {Object.entries(byStatus).map(([status, count]) => (
              <Tile
                key={status}
                label={t("admin.gtcc.ask_genius.label.status")}
                value={`${status}: ${formatCount(count)}`}
                ariaLabel={`${t("admin.gtcc.ask_genius.label.status")} ${status}: ${formatCount(count)}`}
              />
            ))}
          </TileGrid>
        );
      }}
    />
  );
}

interface RiskRadarResponse {
  readonly radar: {
    readonly summary: {
      readonly byLevel: Record<string, number>;
      readonly totalTenants: number;
      readonly instrumentedTenants: number;
      readonly notInstrumentedTenants: number;
    };
  };
}

function RiskRadarSummary({ enabled }: { readonly enabled: boolean }) {
  const { t } = useLocale();
  const query = useQuery<RiskRadarResponse, Error>({
    queryKey: ["gtcc", "ask-genius", "risk-radar"],
    queryFn: ({ signal }) =>
      apiGet<RiskRadarResponse>("/v1/admin/ai/risk-radar", undefined, signal),
    enabled,
  });
  return (
    <QuerySection
      query={query}
      loadingLabel={t("admin.gtcc.ask_genius.risk.loading")}
      errorTitle={t("admin.gtcc.ask_genius.risk.error")}
      emptyTitle={t("admin.gtcc.ask_genius.risk.empty")}
      isEmpty={(data) => data.radar.summary.totalTenants === 0}
      render={(data) => (
        <TileGrid testId="gtcc-ask-genius-summary-risk">
          <Tile
            label={t("admin.gtcc.ask_genius.risk.tenants_assessed_label")}
            value={t("admin.gtcc.ask_genius.risk.tenants_assessed_value", {
              count: data.radar.summary.totalTenants,
            })}
          />
          <Tile
            label={t("admin.gtcc.ask_genius.risk.instrumentation_label")}
            value={t("admin.gtcc.ask_genius.risk.instrumentation_value", {
              instrumented: formatCount(data.radar.summary.instrumentedTenants),
              notInstrumented: formatCount(data.radar.summary.notInstrumentedTenants),
            })}
          />
          {Object.entries(data.radar.summary.byLevel).map(([level, count]) => (
            <Tile
              key={level}
              label={t("admin.gtcc.ask_genius.risk.level_label")}
              value={`${level}: ${formatCount(count)}`}
              ariaLabel={`${t("admin.gtcc.ask_genius.risk.level_label")} ${level}: ${formatCount(count)}`}
            />
          ))}
        </TileGrid>
      )}
    />
  );
}

interface DiagnosticsSnapshotResponse {
  readonly readyz: { readonly status: "ready" | "degraded" | "unready" };
  readonly capturedAt: string;
}
interface RecentErrorsResponse {
  readonly items: ReadonlyArray<unknown>;
}

function DiagnosticsSummary({ enabled }: { readonly enabled: boolean }) {
  const { t } = useLocale();
  const snapshotQuery = useQuery<DiagnosticsSnapshotResponse, Error>({
    queryKey: ["gtcc", "ask-genius", "diagnostics-snapshot"],
    queryFn: ({ signal }) =>
      apiGet<DiagnosticsSnapshotResponse>("/v1/admin/diagnostics/snapshot", undefined, signal),
    enabled,
  });
  const errorsQuery = useQuery<RecentErrorsResponse, Error>({
    queryKey: ["gtcc", "ask-genius", "recent-errors"],
    queryFn: ({ signal }) =>
      apiGet<RecentErrorsResponse>("/v1/admin/recent-errors?limit=5", undefined, signal),
    enabled,
  });
  return (
    <TileGrid testId="gtcc-ask-genius-summary-diag">
      <QuerySection
        query={snapshotQuery}
        loadingLabel={t("admin.gtcc.ask_genius.diag.readiness_loading")}
        errorTitle={t("admin.gtcc.ask_genius.diag.readiness_error")}
        emptyTitle={t("admin.gtcc.ask_genius.diag.readiness_empty")}
        isEmpty={() => false}
        render={(data) => (
          <Tile
            label={t("admin.gtcc.ask_genius.diag.readiness_label")}
            value={t("admin.gtcc.ask_genius.diag.readiness_value", {
              status: data.readyz.status,
              capturedAt: data.capturedAt,
            })}
          />
        )}
      />
      <QuerySection
        query={errorsQuery}
        loadingLabel={t("admin.gtcc.ask_genius.diag.errors_loading")}
        errorTitle={t("admin.gtcc.ask_genius.diag.errors_error")}
        emptyTitle={t("admin.gtcc.ask_genius.diag.errors_empty")}
        isEmpty={() => false}
        render={(data) => (
          <div>
            <Tile
              label={t("admin.gtcc.ask_genius.diag.errors_label")}
              value={t("admin.gtcc.ask_genius.diag.errors_value", { count: data.items.length })}
            />
            <GtccCapHint limit={5} shown={data.items.length} testId="gtcc-ask-genius-errors-cap" />
          </div>
        )}
      />
    </TileGrid>
  );
}

function DataPlaneSummary({ enabled }: { readonly enabled: boolean }) {
  const { t } = useLocale();
  const overviewQuery = useQuery<DataPlaneHealthOverview, Error>({
    queryKey: ["gtcc", "ask-genius", "data-plane-overview"],
    queryFn: ({ signal }) => fetchDataPlaneOverview(signal),
    enabled,
  });
  const readinessQuery = useQuery<DbOpsReadinessReport, Error>({
    queryKey: ["gtcc", "ask-genius", "db-ops-readiness"],
    queryFn: ({ signal }) => fetchDbOpsReadiness(signal),
    enabled,
  });
  return (
    <TileGrid testId="gtcc-ask-genius-summary-dplane">
      <QuerySection
        query={overviewQuery}
        loadingLabel={t("admin.gtcc.ask_genius.dplane.overview_loading")}
        errorTitle={t("admin.gtcc.ask_genius.dplane.overview_error")}
        emptyTitle={t("admin.gtcc.ask_genius.dplane.overview_empty")}
        isEmpty={(data) => data.datastores.length === 0}
        render={(data) => (
          <>
            {Object.entries(data.summary.datastores).map(([status, count]) => (
              <Tile
                key={status}
                label={t("admin.gtcc.ask_genius.dplane.datastore_label")}
                value={`${status}: ${formatCount(count)}`}
              />
            ))}
          </>
        )}
      />
      <QuerySection
        query={readinessQuery}
        loadingLabel={t("admin.gtcc.ask_genius.dplane.readiness_loading")}
        errorTitle={t("admin.gtcc.ask_genius.dplane.readiness_error")}
        emptyTitle={t("admin.gtcc.ask_genius.dplane.readiness_empty")}
        isEmpty={(data) => data.summary.signalCount === 0}
        render={(data) => (
          <Tile
            label={t("admin.gtcc.ask_genius.dplane.readiness_label")}
            value={`${data.summary.overallStatus} — ${t("admin.gtcc.ask_genius.dplane.blockers", {
              count: data.summary.blockerCount,
            })}, ${t("admin.gtcc.ask_genius.dplane.warnings", { count: data.summary.warningCount })}`}
          />
        )}
      />
    </TileGrid>
  );
}

interface ProviderRegistryResponse {
  readonly registry: ProviderRegistry;
}

function AiaSummary({ enabled }: { readonly enabled: boolean }) {
  const { t } = useLocale();
  // Note: `/v1/admin/ai/gfi-readiness` requires a tenantId and has no
  // platform-wide variant, so it cannot be summarized here without a
  // selected tenant — this view only binds the provider registry.
  const query = useQuery<ProviderRegistryResponse, Error>({
    queryKey: ["gtcc", "ask-genius", "provider-registry"],
    queryFn: ({ signal }) =>
      apiGet<ProviderRegistryResponse>("/v1/admin/ai/provider-registry", undefined, signal),
    enabled,
  });
  return (
    <QuerySection
      query={query}
      loadingLabel={t("admin.gtcc.ask_genius.aia.loading")}
      errorTitle={t("admin.gtcc.ask_genius.aia.error")}
      emptyTitle={t("admin.gtcc.ask_genius.aia.empty")}
      isEmpty={(data) => data.registry.providers.length === 0}
      render={(data) => (
        <TileGrid testId="gtcc-ask-genius-summary-aia">
          {data.registry.providers.map((provider) => (
            <Tile
              key={provider.id}
              label={t("admin.gtcc.ask_genius.aia.provider_label")}
              value={`${provider.displayName}: ${
                provider.enabledInCode
                  ? t("admin.gtcc.ask_genius.aia.enabled")
                  : t("admin.gtcc.ask_genius.aia.disabled")
              }`}
            />
          ))}
        </TileGrid>
      )}
    />
  );
}

interface TenantRowDto {
  readonly subscriptionStatus: string;
}
interface TenantsListResponse {
  readonly items: readonly TenantRowDto[];
}
interface PurgeJobDto {
  readonly completedAt: string | null;
}
interface PurgeJobsResponse {
  readonly items: readonly PurgeJobDto[];
}

function TenantsSummary({ enabled }: { readonly enabled: boolean }) {
  const { t } = useLocale();
  const tenantsQuery = useQuery<TenantsListResponse, Error>({
    queryKey: ["gtcc", "ask-genius", "tenants"],
    queryFn: ({ signal }) => apiGet<TenantsListResponse>("/v1/admin/tenants", undefined, signal),
    enabled,
  });
  const purgeQuery = useQuery<PurgeJobsResponse, Error>({
    queryKey: ["gtcc", "ask-genius", "tenants-purge-jobs"],
    queryFn: ({ signal }) =>
      apiGet<PurgeJobsResponse>("/v1/admin/tenants/purge-jobs", undefined, signal),
    enabled,
  });
  return (
    <TileGrid testId="gtcc-ask-genius-summary-ten">
      <QuerySection
        query={tenantsQuery}
        loadingLabel={t("admin.gtcc.ask_genius.ten.loading")}
        errorTitle={t("admin.gtcc.ask_genius.ten.error")}
        emptyTitle={t("admin.gtcc.ask_genius.no_tenants_to_summarize")}
        isEmpty={(data) => data.items.length === 0}
        render={(data) => {
          const byStatus = data.items.reduce<Record<string, number>>((acc, row) => {
            acc[row.subscriptionStatus] = (acc[row.subscriptionStatus] ?? 0) + 1;
            return acc;
          }, {});
          return (
            <>
              <Tile
                label={t("admin.gtcc.ask_genius.label.tenants")}
                value={t("admin.gtcc.ask_genius.tenants_count", { count: data.items.length })}
              />
              {Object.entries(byStatus).map(([status, count]) => (
                <Tile
                  key={status}
                  label={t("admin.gtcc.ask_genius.label.status")}
                  value={`${status}: ${formatCount(count)}`}
                />
              ))}
            </>
          );
        }}
      />
      <QuerySection
        query={purgeQuery}
        loadingLabel={t("admin.gtcc.ask_genius.purge.loading")}
        errorTitle={t("admin.gtcc.ask_genius.purge.error")}
        emptyTitle={t("admin.gtcc.ask_genius.purge.empty")}
        isEmpty={() => false}
        render={(data) => {
          const open = data.items.filter((job) => job.completedAt === null).length;
          return (
            <Tile
              label={t("admin.gtcc.ask_genius.purge.label")}
              value={t("admin.gtcc.ask_genius.ten.purge_open_value", { count: open })}
            />
          );
        }}
      />
    </TileGrid>
  );
}

interface PoolListResponse {
  readonly items: readonly unknown[];
  readonly truncated: boolean;
}
interface SimsListResponse {
  readonly items: readonly unknown[];
  readonly truncated: boolean;
}
interface PendingDevicesResponse {
  readonly items: readonly unknown[];
}

function HardwareSummary({ enabled }: { readonly enabled: boolean }) {
  const { t } = useLocale();
  const poolQuery = useQuery<PoolListResponse, Error>({
    queryKey: ["gtcc", "ask-genius", "managed-hardware-pool"],
    queryFn: ({ signal }) =>
      apiGet<PoolListResponse>("/v1/admin/managed-hardware/pool", { limit: 200 }, signal),
    enabled,
  });
  const simsQuery = useQuery<SimsListResponse, Error>({
    queryKey: ["gtcc", "ask-genius", "sims"],
    queryFn: ({ signal }) => apiGet<SimsListResponse>("/v1/admin/sims", { limit: 200 }, signal),
    enabled,
  });
  const pendingQuery = useQuery<PendingDevicesResponse, Error>({
    queryKey: ["gtcc", "ask-genius", "devices-pending"],
    queryFn: ({ signal }) =>
      apiGet<PendingDevicesResponse>("/v1/admin/devices/pending", undefined, signal),
    enabled,
  });
  return (
    <TileGrid testId="gtcc-ask-genius-summary-hw">
      <QuerySection
        query={poolQuery}
        loadingLabel={t("admin.gtcc.ask_genius.hw.pool_loading")}
        errorTitle={t("admin.gtcc.ask_genius.hw.pool_error")}
        emptyTitle={t("admin.gtcc.ask_genius.hw.pool_empty")}
        isEmpty={(data) => data.items.length === 0}
        render={(data) => (
          <Tile
            label={t("admin.gtcc.ask_genius.hw.pool_label")}
            value={t("admin.gtcc.ask_genius.hw.pool_value", { count: data.items.length })}
            note={data.truncated ? t("admin.gtcc.ask_genius.count_truncated") : undefined}
          />
        )}
      />
      <QuerySection
        query={simsQuery}
        loadingLabel={t("admin.gtcc.ask_genius.hw.sims_loading")}
        errorTitle={t("admin.gtcc.ask_genius.hw.sims_error")}
        emptyTitle={t("admin.gtcc.ask_genius.hw.sims_empty")}
        isEmpty={(data) => data.items.length === 0}
        render={(data) => (
          <Tile
            label={t("admin.gtcc.ask_genius.hw.sims_label")}
            value={t("admin.gtcc.ask_genius.hw.sims_value", { count: data.items.length })}
            note={data.truncated ? t("admin.gtcc.ask_genius.count_truncated") : undefined}
          />
        )}
      />
      <QuerySection
        query={pendingQuery}
        loadingLabel={t("admin.gtcc.ask_genius.hw.pending_loading")}
        errorTitle={t("admin.gtcc.ask_genius.hw.pending_error")}
        emptyTitle={t("admin.gtcc.ask_genius.hw.pending_empty")}
        isEmpty={(data) => data.items.length === 0}
        render={(data) => (
          <Tile
            label={t("admin.gtcc.ask_genius.hw.pending_label")}
            value={t("admin.gtcc.ask_genius.hw.pending_value", { count: data.items.length })}
          />
        )}
      />
    </TileGrid>
  );
}

interface IngestSnapshotResponse {
  readonly status: "ok" | "near_limit" | "limited";
  readonly windows: {
    readonly "24h": { readonly accepted: number; readonly rateLimited: number };
  };
}
interface GatewayRejectionsResponse {
  readonly counts: Record<string, number>;
}

function GatewaySummary({ enabled }: { readonly enabled: boolean }) {
  const { t } = useLocale();
  const ingestQuery = useQuery<IngestSnapshotResponse, Error>({
    queryKey: ["gtcc", "ask-genius", "gateway-ingest"],
    queryFn: ({ signal }) =>
      apiGet<IngestSnapshotResponse>("/v1/admin/gateway/ingest", undefined, signal),
    enabled,
  });
  const rejectionsQuery = useQuery<GatewayRejectionsResponse, Error>({
    queryKey: ["gtcc", "ask-genius", "gateway-rejections"],
    queryFn: ({ signal }) =>
      apiGet<GatewayRejectionsResponse>("/v1/admin/gateway/rejections", undefined, signal),
    enabled,
  });
  return (
    <TileGrid testId="gtcc-ask-genius-summary-gw">
      <QuerySection
        query={ingestQuery}
        loadingLabel={t("admin.gtcc.ask_genius.gw.ingest_loading")}
        errorTitle={t("admin.gtcc.ask_genius.gw.ingest_error")}
        emptyTitle={t("admin.gtcc.ask_genius.gw.ingest_empty")}
        isEmpty={() => false}
        render={(data) => (
          <Tile
            label={t("admin.gtcc.ask_genius.gw.ingest_label")}
            value={t("admin.gtcc.ask_genius.gw.ingest_value", {
              status: data.status,
              accepted: formatCount(data.windows["24h"].accepted),
              rateLimited: formatCount(data.windows["24h"].rateLimited),
            })}
          />
        )}
      />
      <QuerySection
        query={rejectionsQuery}
        loadingLabel={t("admin.gtcc.ask_genius.gw.rejections_loading")}
        errorTitle={t("admin.gtcc.ask_genius.gw.rejections_error")}
        emptyTitle={t("admin.gtcc.ask_genius.gw.rejections_empty")}
        isEmpty={(data) => Object.keys(data.counts).length === 0}
        render={(data) => {
          const total = Object.values(data.counts).reduce((sum, n) => sum + n, 0);
          return (
            <Tile
              label={t("admin.gtcc.ask_genius.gw.rejections_label")}
              value={t("admin.gtcc.ask_genius.gw.rejections_value", { count: total })}
            />
          );
        }}
      />
    </TileGrid>
  );
}

function DomainRoutingSummary({ enabled }: { readonly enabled: boolean }) {
  const { t } = useLocale();
  const query = useQuery<DomainRoutingHealthResponse, Error>({
    queryKey: ["gtcc", "ask-genius", "domain-routing"],
    queryFn: ({ signal }) =>
      apiGet<DomainRoutingHealthResponse>("/v1/admin/domain-routing/health", undefined, signal),
    enabled,
  });

  return (
    <QuerySection
      query={query}
      loadingLabel={t("admin.gtcc.ask_genius.dom.loading")}
      errorTitle={t("admin.gtcc.ask_genius.dom.error")}
      emptyTitle={t("admin.gtcc.ask_genius.dom.empty")}
      isEmpty={(data) => !data.readiness}
      render={(data) => (
        <TileGrid testId="gtcc-ask-genius-summary-dom">
          <Tile
            label={t("admin.gtcc.ask_genius.dom.status_label")}
            value={t(`admin.domain_routing.status.${data.readiness.status}` as const)}
          />
          <Tile
            label={t("admin.gtcc.ask_genius.dom.warnings_label")}
            value={String(data.warnings.length)}
          />
          <Tile
            label={t("admin.gtcc.ask_genius.dom.allowlist_label")}
            value={String(data.readiness.allowedHostCount)}
          />
        </TileGrid>
      )}
    />
  );
}

interface MailHealthResponse {
  readonly provider: string;
  readonly readiness: { readonly realSendConfigured: boolean };
}
interface EmailDeliveriesResponse {
  readonly total: number;
}
interface TelegramHealthResponse {
  readonly readiness: { readonly status: string };
  readonly linkedChatsCount: number;
}

function MailSummary({ enabled }: { readonly enabled: boolean }) {
  const { t } = useLocale();
  const mailQuery = useQuery<MailHealthResponse, Error>({
    queryKey: ["gtcc", "ask-genius", "mail-health"],
    queryFn: ({ signal }) => apiGet<MailHealthResponse>("/v1/admin/mail/health", undefined, signal),
    enabled,
  });
  const deliveriesQuery = useQuery<EmailDeliveriesResponse, Error>({
    queryKey: ["gtcc", "ask-genius", "email-deliveries"],
    queryFn: ({ signal }) =>
      apiGet<EmailDeliveriesResponse>("/v1/admin/email-deliveries", undefined, signal),
    enabled,
  });
  const telegramQuery = useQuery<TelegramHealthResponse, Error>({
    queryKey: ["gtcc", "ask-genius", "telegram-health"],
    queryFn: ({ signal }) =>
      apiGet<TelegramHealthResponse>("/v1/admin/telegram/health", undefined, signal),
    enabled,
  });
  return (
    <TileGrid testId="gtcc-ask-genius-summary-mail">
      <QuerySection
        query={mailQuery}
        loadingLabel={t("admin.gtcc.ask_genius.mail.health_loading")}
        errorTitle={t("admin.gtcc.ask_genius.mail.health_error")}
        emptyTitle={t("admin.gtcc.ask_genius.mail.health_empty")}
        isEmpty={() => false}
        render={(data) => (
          <Tile
            label={t("admin.gtcc.ask_genius.mail.provider_label")}
            value={t("admin.gtcc.ask_genius.mail.provider_value", {
              provider: data.provider,
              configured: data.readiness.realSendConfigured
                ? t("admin.gtcc.ask_genius.mail.real_send_configured")
                : t("admin.gtcc.ask_genius.mail.log_only"),
            })}
          />
        )}
      />
      <QuerySection
        query={deliveriesQuery}
        loadingLabel={t("admin.gtcc.ask_genius.mail.deliveries_loading")}
        errorTitle={t("admin.gtcc.ask_genius.mail.deliveries_error")}
        emptyTitle={t("admin.gtcc.ask_genius.mail.deliveries_empty")}
        isEmpty={(data) => data.total === 0}
        render={(data) => (
          <Tile
            label={t("admin.gtcc.ask_genius.mail.deliveries_label")}
            value={t("admin.gtcc.ask_genius.mail.deliveries_value", { count: data.total })}
          />
        )}
      />
      <QuerySection
        query={telegramQuery}
        loadingLabel={t("admin.gtcc.ask_genius.mail.telegram_loading")}
        errorTitle={t("admin.gtcc.ask_genius.mail.telegram_error")}
        emptyTitle={t("admin.gtcc.ask_genius.mail.telegram_empty")}
        isEmpty={() => false}
        render={(data) => (
          <Tile
            label={t("admin.gtcc.ask_genius.mail.telegram_label")}
            value={`Telegram: ${data.readiness.status} — ${t(
              "admin.gtcc.ask_genius.mail.telegram_chats",
              {
                count: data.linkedChatsCount,
              },
            )}`}
          />
        )}
      />
    </TileGrid>
  );
}

interface DeliveryHealthSummaryResponse {
  readonly deliveryHealth: {
    readonly categories: ReadonlyArray<{
      readonly status: "ok" | "warning" | "critical" | "partial" | "not_instrumented";
    }>;
  };
}

function NotificationsSummary({ enabled }: { readonly enabled: boolean }) {
  const { t } = useLocale();
  const query = useQuery<DeliveryHealthSummaryResponse, Error>({
    queryKey: ["gtcc", "ask-genius", "delivery-health"],
    queryFn: ({ signal }) =>
      apiGet<DeliveryHealthSummaryResponse>("/v1/admin/ai/delivery-health", undefined, signal),
    enabled,
  });
  return (
    <QuerySection
      query={query}
      loadingLabel={t("admin.gtcc.ask_genius.notifications.loading")}
      errorTitle={t("admin.gtcc.ask_genius.notifications.error")}
      emptyTitle={t("admin.gtcc.ask_genius.notifications.empty")}
      isEmpty={(data) => data.deliveryHealth.categories.length === 0}
      render={(data) => {
        const categories = data.deliveryHealth.categories;
        const healthy = categories.filter((category) => category.status === "ok").length;
        const notInstrumented = categories.filter(
          (category) => category.status === "not_instrumented",
        ).length;
        const attention = categories.length - healthy - notInstrumented;
        return (
          <TileGrid testId="gtcc-ask-genius-summary-notifications">
            <Tile
              label={t("admin.gtcc.ask_genius.notifications.healthy_label")}
              value={t("admin.gtcc.ask_genius.notifications.healthy_value", { count: healthy })}
            />
            <Tile
              label={t("admin.gtcc.ask_genius.notifications.attention_label")}
              value={t("admin.gtcc.ask_genius.notifications.attention_value", {
                count: attention,
              })}
            />
            <Tile
              label={t("admin.gtcc.ask_genius.notifications.not_instrumented_label")}
              value={t("admin.gtcc.ask_genius.notifications.not_instrumented_value", {
                count: notInstrumented,
              })}
            />
          </TileGrid>
        );
      }}
    />
  );
}

function SecuritySummary({ enabled }: { readonly enabled: boolean }) {
  const { t } = useLocale();
  return (
    <div data-testid="gtcc-ask-genius-summary-security">
      <RiskRadarSummary enabled={enabled} />
      <TileGrid testId="gtcc-ask-genius-summary-security-grants">
        <Tile
          label={t("admin.gtcc.ask_genius.security.coverage_label")}
          value={t("admin.gtcc.ask_genius.security.coverage_value")}
        />
      </TileGrid>
    </div>
  );
}

interface AuditEntryDto {
  readonly action: string;
  readonly createdAt: string;
}
interface ListAuditOutput {
  readonly items: readonly AuditEntryDto[];
  readonly nextCursor: string | null;
}

function AuditSummary({ enabled }: { readonly enabled: boolean }) {
  const { t } = useLocale();
  const query = useQuery<ListAuditOutput, Error>({
    queryKey: ["gtcc", "ask-genius", "audit"],
    queryFn: ({ signal }) => apiGet<ListAuditOutput>("/v1/admin/audit", { limit: 200 }, signal),
    enabled,
  });
  return (
    <QuerySection
      query={query}
      loadingLabel={t("admin.gtcc.ask_genius.audit.loading")}
      errorTitle={t("admin.gtcc.ask_genius.audit.error")}
      emptyTitle={t("admin.gtcc.ask_genius.audit.empty")}
      isEmpty={(data) => data.items.length === 0}
      render={(data) => (
        <TileGrid testId="gtcc-ask-genius-summary-audit">
          <Tile
            label={t("admin.gtcc.ask_genius.audit.entries_label")}
            value={t("admin.gtcc.ask_genius.audit.entries_value", { count: data.items.length })}
            note={data.nextCursor ? t("admin.gtcc.ask_genius.count_truncated") : undefined}
          />
          <Tile
            label={t("admin.gtcc.ask_genius.label.most_recent")}
            value={t("admin.gtcc.ask_genius.most_recent_value", {
              timestamp: formatIsoOrDash(data.items[0]?.createdAt),
            })}
          />
        </TileGrid>
      )}
    />
  );
}

interface MonitoringSummaryQueryResult {
  readonly overview: MonitoringSummaryDto["overview"];
}

function AlinSummary({ enabled }: { readonly enabled: boolean }) {
  const { t } = useLocale();
  const query = useQuery<MonitoringSummaryQueryResult, Error>({
    queryKey: ["gtcc", "ask-genius", "monitoring-summary"],
    queryFn: ({ signal }) =>
      apiGet<MonitoringSummaryDto>(MONITORING_SUMMARY_PATH, undefined, signal),
    enabled,
  });
  return (
    <QuerySection
      query={query}
      loadingLabel={t("admin.gtcc.ask_genius.alin.loading")}
      errorTitle={t("admin.gtcc.ask_genius.alin.error")}
      emptyTitle={t("admin.gtcc.ask_genius.alin.empty")}
      isEmpty={() => false}
      render={(data) => (
        <TileGrid testId="gtcc-ask-genius-summary-alin">
          <Tile
            label={t("admin.gtcc.ask_genius.alin.critical_label")}
            value={t("admin.gtcc.ask_genius.alin.critical_value", {
              count: data.overview.criticalAlerts,
            })}
          />
          <Tile
            label={t("admin.gtcc.ask_genius.alin.warning_label")}
            value={t("admin.gtcc.ask_genius.alin.warning_value", {
              count: data.overview.warningAlerts,
            })}
          />
          <Tile
            label={t("admin.gtcc.ask_genius.label.most_recent")}
            value={t("admin.gtcc.ask_genius.most_recent_value", {
              timestamp: formatIsoOrDash(data.overview.lastCheckedAt),
            })}
          />
        </TileGrid>
      )}
    />
  );
}

function EntitlementsBlock({ enabled }: { readonly enabled: boolean }) {
  const { t } = useLocale();
  const entitlementsQuery = useQuery<ResolvedEntitlementsView, Error>({
    queryKey: ["gtcc", "ask-genius", "entitlements"],
    queryFn: ({ signal }) =>
      apiGet<ResolvedEntitlementsView>("/v1/admin/entitlements", undefined, signal),
    enabled,
  });
  return (
    <QuerySection
      query={entitlementsQuery}
      loadingLabel={t("admin.gtcc.ask_genius.entitlements.loading")}
      errorTitle={t("admin.gtcc.ask_genius.entitlements.error")}
      emptyTitle={t("admin.gtcc.ask_genius.entitlements.empty")}
      isEmpty={(data) => data.resolved.entries.length === 0}
      render={(data) => (
        <Tile
          label={t("admin.gtcc.ask_genius.entitlements.label")}
          value={`Plan: ${t(planNameKey(data.plan))} — ${t(
            "admin.gtcc.ask_genius.entitlements.entries",
            {
              count: data.resolved.entries.length,
            },
          )}, ${t("admin.gtcc.ask_genius.entitlements.overrides", { count: data.overrides.length })}`}
        />
      )}
    />
  );
}

function CockpitSummary({ enabled }: { readonly enabled: boolean }) {
  const { t } = useLocale();
  return (
    <div data-testid="gtcc-ask-genius-summary-cockpit">
      <section>
        <h3>{t("admin.gtcc.ask_genius.label.tenants")}</h3>
        <TenantsSummary enabled={enabled} />
      </section>
      <section>
        <h3>{t("admin.gtcc.ask_genius.cockpit.hardware_heading")}</h3>
        <HardwareSummary enabled={enabled} />
      </section>
      <section>
        <h3>{t("admin.gtcc.ask_genius.cockpit.gateways_heading")}</h3>
        <GatewaySummary enabled={enabled} />
      </section>
      <section>
        <h3>{t("admin.gtcc.ask_genius.cockpit.audit_heading")}</h3>
        <AuditSummary enabled={enabled} />
      </section>
      <section>
        <h3>{t("admin.gtcc.ask_genius.entitlements.label")}</h3>
        <EntitlementsBlock enabled={enabled} />
      </section>
    </div>
  );
}

interface ResolvedOverrideRow {
  readonly key: string;
}
interface ResolvedEntitlementsView {
  readonly plan: PlanId;
  readonly overrides: readonly ResolvedOverrideRow[];
  readonly resolved: {
    readonly entries: readonly string[];
  };
}
interface TenancySettingsResponse {
  readonly demoApplyAllowed?: boolean;
  readonly [key: string]: unknown;
}

function PlatformSettingsSummary({ enabled }: { readonly enabled: boolean }) {
  const { t } = useLocale();
  const flagsQuery = useQuery<FeatureFlagsResponse, Error>({
    queryKey: FEATURE_FLAGS_KEY,
    queryFn: ({ signal }) => apiGet<FeatureFlagsResponse>("/v1/feature-flags", undefined, signal),
    enabled,
  });
  const settingsQuery = useQuery<TenancySettingsResponse, Error>({
    queryKey: ["gtcc", "ask-genius", "tenancy-settings"],
    queryFn: ({ signal }) =>
      apiGet<TenancySettingsResponse>("/v1/tenancy/settings", undefined, signal),
    enabled,
  });
  return (
    <TileGrid testId="gtcc-ask-genius-summary-pset">
      <EntitlementsBlock enabled={enabled} />
      <QuerySection
        query={flagsQuery}
        loadingLabel={t("admin.gtcc.ask_genius.pset.flags_loading")}
        errorTitle={t("admin.gtcc.ask_genius.pset.flags_error")}
        emptyTitle={t("admin.gtcc.ask_genius.pset.flags_empty")}
        isEmpty={(data) => data.flags.length === 0}
        render={(data) => {
          const enabledCount = data.flags.filter((f) => f.enabled).length;
          return (
            <Tile
              label={t("admin.gtcc.ask_genius.pset.flags_label")}
              value={`${t("admin.gtcc.ask_genius.pset.flags_value", { count: data.flags.length })}, ${t(
                "admin.gtcc.ask_genius.pset.flags_enabled_suffix",
                { count: enabledCount },
              )}`}
            />
          );
        }}
      />
      <QuerySection
        query={settingsQuery}
        loadingLabel={t("admin.gtcc.ask_genius.pset.settings_loading")}
        errorTitle={t("admin.gtcc.ask_genius.pset.settings_error")}
        emptyTitle={t("admin.gtcc.ask_genius.pset.settings_empty")}
        isEmpty={(data) => Object.keys(data).length === 0}
        render={(data) => (
          <Tile
            label={t("admin.gtcc.ask_genius.pset.settings_label")}
            value={`${t("admin.gtcc.ask_genius.pset.settings_value", {
              count: Object.keys(data).length,
            })}${
              data.demoApplyAllowed !== undefined
                ? `, ${
                    data.demoApplyAllowed
                      ? t("admin.gtcc.ask_genius.pset.demo_apply_allowed")
                      : t("admin.gtcc.ask_genius.pset.demo_apply_blocked")
                  }`
                : ""
            }`}
          />
        )}
      />
    </TileGrid>
  );
}

interface PurgeJobSummaryDto {
  readonly id: string;
  readonly completedAt: string | null;
}

function DangerZoneSummary({ enabled }: { readonly enabled: boolean }) {
  const { t } = useLocale();
  const query = useQuery<{ items: readonly PurgeJobSummaryDto[] }, Error>({
    queryKey: ["gtcc", "ask-genius", "purge-jobs"],
    queryFn: ({ signal }) =>
      apiGet<{ items: readonly PurgeJobSummaryDto[] }>(
        "/v1/admin/tenants/purge-jobs",
        undefined,
        signal,
      ),
    enabled,
  });
  return (
    <QuerySection
      query={query}
      loadingLabel={t("admin.gtcc.ask_genius.purge.loading")}
      errorTitle={t("admin.gtcc.ask_genius.purge.error")}
      emptyTitle={t("admin.gtcc.ask_genius.purge.empty")}
      isEmpty={(data) => data.items.length === 0}
      render={(data) => {
        const inProgress = data.items.filter((job) => job.completedAt === null).length;
        return (
          <TileGrid testId="gtcc-ask-genius-summary-dz">
            <Tile
              label={t("admin.gtcc.ask_genius.purge.label")}
              value={`${t("admin.gtcc.ask_genius.dz.purge_jobs", { count: data.items.length })}, ${t(
                "admin.gtcc.ask_genius.dz.in_progress",
                { count: inProgress },
              )}`}
            />
          </TileGrid>
        );
      }}
    />
  );
}

function SmokeSummary({ enabled }: { readonly enabled: boolean }) {
  const { t } = useLocale();
  const queries = useGtccSmokeQueries(enabled);
  const anyPending = Object.values(queries).some((query) => query.isPending);
  if (!enabled || anyPending) {
    return <LoadingState label={t("admin.gtcc.ask_genius.smoke.loading")} />;
  }
  const tally = tallySmokeQueries(queries);
  return (
    <TileGrid testId="gtcc-ask-genius-summary-smoke">
      <Tile
        label={t("admin.gtcc.ask_genius.smoke.label")}
        value={`${t("admin.gtcc.smoke.summary.green", { count: tally.green })}, ${t(
          "admin.gtcc.smoke.summary.yellow",
          { count: tally.yellow },
        )}, ${t("admin.gtcc.smoke.summary.red", { count: tally.red })}`}
      />
    </TileGrid>
  );
}
